import React from 'react';
import CustomCardScene, { isDarkColor } from '../gift-cards-v2/CustomCardScene';
import AbstractScene from '../gift-cards-v2/AbstractScene';
import { themeById, iconById, SCALLOP_PATH, BLOB_PATH, OVAL_PATH, CIRCLE_PATH, SKETCH_PATH } from './data';
import './LoyaltyCard.css';

/**
 * The loyalty card: a salon-branded, wallet-grade artefact rendered from
 * design + scheme + runtime state (never a baked image, so changing the
 * design updates every client's card instantly).
 *
 * design: { themeId, icon, layout, fillStyle, orientation, stampShape,
 *           header ('classic'|'centre'|'none'), footer ('standard'|'hidden'),
 *           custom { bgMode..., elements } }
 */

const ROW_MAX = 5;
const PORTRAIT_ROW_MAX = 3;

const SHAPE_PATHS = { scallop: SCALLOP_PATH, blob: BLOB_PATH, oval: OVAL_PATH, circle: CIRCLE_PATH, sketch: SKETCH_PATH };

// Icon entries are either { svg } (stroke icon) or { emoji }.
const StampGlyph = ({ icon, dim }) => (icon.emoji ? (
  <span style={{ fontSize: '1.1em', lineHeight: 1, opacity: dim ? 0.45 : 1 }} aria-hidden>{icon.emoji}</span>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{icon.svg}</svg>
));

const RewardGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <g><rect x="3" y="8" width="18" height="4" /><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8M12 8v13" /><path d="M12 8a3 3 0 1 0-3-5c1.5 0 3 2 3 5zM12 8a3 3 0 1 1 3-5c-1.5 0-3 2-3 5z" /></g>
  </svg>
);

const LoyaltyCard = ({
  design, scheme, state = {}, salonName = 'Your Salon', width = 360, animateIndex = -1,
  // Decoration-canvas editing (designer only): pass-through to CustomCardScene
  editable = false, selectedId = null, selectedIds = [], editingId = null,
  onElementSelect, onElementChange, onElementsChange, onElementDuplicate,
  onElementRemove, onElementDoubleClick, onElementContextMenu, onElementCommitEdit,
  onConvertHeader,
}) => {
  const theme = themeById(design?.themeId);
  // Stamps (and the points ring) use the theme accent unless the salon has
  // picked an explicit stamp colour in the Stamps panel. Glyph colour
  // follows the accent's contrast so any picked colour stays legible.
  const accent = design?.stampColor || theme.accent;
  const onAccent = isDarkColor(accent) ? '#F7F5F0' : '#1A1A1E';
  const icon = iconById(design?.icon);
  const portrait = design?.orientation === 'portrait';
  const header = design?.header || 'classic';
  const showFooter = design?.footer !== 'hidden' && design?.footer !== 'custom';
  const sticker = design?.fillStyle === 'sticker';
  const shapePath = sticker
    ? (SHAPE_PATHS[design?.stampShape] || CIRCLE_PATH)
    : (design?.stampShape && design.stampShape !== 'circle' ? SHAPE_PATHS[design.stampShape] : null);
  const custom = design?.custom || null;
  const hasCustomBg = !!(custom && custom.bgMode);
  const hasElements = !!(custom && custom.elements && custom.elements.length);

  // Ink follows whatever the card actually sits on: theme colour, custom
  // solid/gradient, or light-on-photo (the photo layer adds a scrim).
  let darkSurface = theme.ink === 'light';
  if (hasCustomBg) {
    if (custom.bgMode === 'solid') darkSurface = isDarkColor(custom.bgColor);
    else if (custom.bgMode === 'gradient') darkSurface = isDarkColor(custom.bgGradient?.from || '#29282D');
    else if (custom.bgMode === 'photo') darkSurface = true;
    else darkSurface = theme.ink === 'light';
  }
  const inkColor = darkSurface ? '#F7F5F0' : '#1A1A1E';
  const subInk = darkSurface ? 'rgba(247,245,240,0.55)' : 'rgba(26,26,30,0.5)';
  const isStamps = scheme?.mode !== 'points';
  const required = Math.max(2, Math.min(12, scheme?.stampsRequired || 6));
  const filled = Math.max(0, Math.min(required, state.filled != null ? state.filled : Math.ceil(required / 2)));
  const heroFont = theme.hero === 'serif' ? 'Georgia, serif' : 'Inter, system-ui, sans-serif';

  const tagline = isStamps
    ? '1 visit = 1 stamp'
    : `${scheme?.pointsPerPound || 1} point${(scheme?.pointsPerPound || 1) > 1 ? 's' : ''} per £1`;

  // Points mode progress
  const target = scheme?.pointsTarget || 500;
  const balance = state.balance != null ? state.balance : Math.round(target * 0.55);
  const pct = Math.max(0, Math.min(1, balance / target));
  const R = 30;
  const CIRC = 2 * Math.PI * R;

  const slots = [];
  for (let i = 0; i < required; i += 1) {
    const isReward = i === required - 1;
    const isFilled = i < filled;
    slots.push({ i, isReward, isFilled });
  }
  // Balanced rows. Portrait is narrow, so it caps at 3 per row and treats
  // "single row" as a grid (a horizontal strip cannot fit upright).
  const half = !portrait && (design?.stampBlock === 'left' || design?.stampBlock === 'right');
  const rowMax = portrait || half ? PORTRAIT_ROW_MAX : ROW_MAX;
  const balanced = Math.min(rowMax, Math.ceil(required / Math.ceil(required / rowMax)));
  const isRow = !portrait && design?.layout === 'row';
  const perRow = isRow ? required : balanced;

  const scale = width / (portrait ? 250 : 360);

  // Stamps size themselves to the card: fewer per row = bigger stamps,
  // and a long single row shrinks to fit instead of overflowing.
  const cardEm = portrait ? 250 / (16 * 0.92) : 22.5;
  const innerEm = (cardEm - 3) * (half ? 0.52 : 1); // 1.5em padding each side
  const gapEm = isRow ? 0.5 : (portrait ? 0.85 : 0.65);
  const capEm = perRow <= 3 ? 3.5 : perRow === 4 ? 3.1 : perRow === 5 ? 2.7 : 2.5;
  const stampEm = Math.max(1.4, Math.min(capEm, (innerEm - gapEm * (perRow - 1)) / perRow));
  const sealEm = stampEm * 1.1;

  const renderStamp = ({ i, isReward, isFilled }) => {
    const pressedTilt = design?.fillStyle === 'stamp-ink' && isFilled ? `rotate(${((i * 47) % 13) - 6}deg)` : undefined;
    const stickerTilt = sticker && isFilled ? `rotate(${((i * 31) % 9) - 4}deg)` : undefined;
    const glyph = isReward ? <RewardGlyph /> : (
      design?.slotLabel === 'number' && !isFilled
        ? <span style={{ fontSize: '0.95em', fontWeight: 700, opacity: 0.75 }}>{i + 1}</span>
        : <StampGlyph icon={icon} dim={!isFilled} />
    );
    if (!shapePath) {
      return (
        <span
          key={i}
          className={[
            'lc-stamp',
            isFilled ? 'is-filled' : '',
            isReward ? 'is-reward' : '',
            i === animateIndex ? 'is-popping' : '',
          ].filter(Boolean).join(' ')}
          style={{
            width: `${stampEm}em`,
            height: `${stampEm}em`,
            borderColor: isFilled ? accent : subInk,
            background: isFilled ? accent : 'transparent',
            color: isFilled ? onAccent : subInk,
            transform: pressedTilt,
          }}
        >
          {glyph}
        </span>
      );
    }
    // Seal rendering (scallop / blob / oval, and every shape in sticker style)
    const mainFill = sticker ? '#FFFDF6' : accent;
    const glyphColor = isFilled ? (sticker ? '#2B2B30' : onAccent) : subInk;
    return (
      <span
        key={i}
        className={['lc-stamp', 'lc-stamp--shaped', i === animateIndex ? 'is-popping' : ''].filter(Boolean).join(' ')}
        style={{ width: `${sealEm}em`, height: `${sealEm}em`, color: glyphColor, transform: pressedTilt || stickerTilt }}
      >
        <svg viewBox="0 0 48 48" className="lc-stamp-seal" aria-hidden>
          {sticker && isFilled ? (
            <path d={shapePath} fill={accent} transform="translate(3 3.4)" />
          ) : null}
          <path
            d={shapePath}
            fill={isFilled ? mainFill : 'none'}
            stroke={isFilled ? (sticker ? 'none' : accent) : subInk}
            strokeWidth={isFilled && sticker ? 0 : 2}
          />
        </svg>
        <span className="lc-stamp-glyph">{glyph}</span>
      </span>
    );
  };

  const bgStyle = !hasCustomBg ? { background: theme.bg }
    : custom.bgMode === 'solid' ? { background: custom.bgColor }
    : custom.bgMode === 'gradient' ? { background: `linear-gradient(${custom.bgGradient?.angle ?? 135}deg, ${custom.bgGradient?.from || '#29282D'}, ${custom.bgGradient?.to || '#0D3B4F'})` }
    : { background: theme.bg };

  const sceneConfig = (editable || hasElements) ? {
    bgMode: 'solid',
    bgColor: 'transparent',
    bgGradient: { from: '#29282D', to: '#0D3B4F', angle: 135 },
    bgPhoto: null,
    bgPattern: { occasion: 'birthday' },
    ink: inkColor,
    accent: theme.accent,
    fontFamily: 'fraunces',
    elements: (custom && custom.elements) || [],
  } : null;

  // With the built-in header off, the structured content leaves headroom
  // for the text elements that replace it.
  const headroom = header === 'none' && !portrait ? '4.9em' : undefined;

  // In the designer, the built-in header text converts to editable text
  // elements the moment it is clicked.
  const clickable = editable && onConvertHeader
    ? (which) => ({ onClick: () => onConvertHeader(which), style: { pointerEvents: 'auto', cursor: 'text' } })
    : () => ({});
  const merge = (base, extra) => ({ ...(base || {}), ...(extra.style || {}) });

  return (
    <div
      className={'lc-card' + (editable ? ' lc-card--editing' : '')}
      style={{
        width,
        position: 'relative',
        ...bgStyle,
        color: inkColor,
        fontSize: 16 * scale * (portrait ? 0.92 : 1),
        ...(portrait ? { minHeight: width * 1.42, display: 'flex', flexDirection: 'column' } : {}),
      }}
    >
      {hasCustomBg && custom.bgMode === 'photo' && custom.bgPhoto && custom.bgPhoto.url ? (
        <span className="lc-bg-photo" aria-hidden>
          <img
            src={custom.bgPhoto.url}
            alt=""
            style={{
              objectPosition: `${custom.bgPhoto.focalX ?? 50}% ${custom.bgPhoto.focalY ?? 50}%`,
              transform: `scale(${custom.bgPhoto.zoom || 1})`,
              transformOrigin: `${custom.bgPhoto.focalX ?? 50}% ${custom.bgPhoto.focalY ?? 50}%`,
            }}
          />
          <span className="lc-bg-photo-scrim" />
        </span>
      ) : null}
      {hasCustomBg && custom.bgMode === 'pattern' ? (
        <span className="lc-bg-pattern" aria-hidden>
          <AbstractScene occasion={custom.bgPattern?.occasion || 'birthday'} />
        </span>
      ) : null}

      {header === 'centre' ? (
        <div className="lc-top" style={{ flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '0.25em' }}>
          <span className="lc-tag" onClick={clickable('tag').onClick} style={merge({ color: theme.accent }, clickable('tag'))}>Loyalty</span>
          <span className="lc-salon" onClick={clickable('title').onClick} style={merge({ fontFamily: heroFont, whiteSpace: 'normal', fontSize: '1.35em', lineHeight: 1.15 }, clickable('title'))}>{salonName}</span>
          <span onClick={clickable('tagline').onClick} style={merge({ fontSize: '0.62em', fontWeight: 700, letterSpacing: '0.06em', color: subInk }, clickable('tagline'))}>{tagline}</span>
        </div>
      ) : header === 'classic' ? (
        <div className="lc-top" style={portrait ? { flexDirection: 'column', alignItems: 'flex-start', gap: '0.3em' } : undefined}>
          {portrait ? (
            <>
              <span className="lc-tag" onClick={clickable('tag').onClick} style={merge({ color: theme.accent }, clickable('tag'))}>Loyalty</span>
              <span className="lc-salon" onClick={clickable('title').onClick} style={merge({ fontFamily: heroFont, whiteSpace: 'normal' }, clickable('title'))}>{salonName}</span>
            </>
          ) : (
            <>
              <span className="lc-salon" onClick={clickable('title').onClick} style={merge({ fontFamily: heroFont }, clickable('title'))}>{salonName}</span>
              <span className="lc-tag" onClick={clickable('tag').onClick} style={merge({ color: theme.accent }, clickable('tag'))}>Loyalty</span>
            </>
          )}
        </div>
      ) : null}

      {isStamps ? (
        <>
          <div style={portrait ? { flex: 1, display: 'flex', alignItems: 'center', marginTop: header === 'none' ? '6.5em' : undefined } : { marginTop: headroom }}>
            <div
              className="lc-stamps"
              style={{
                gridTemplateColumns: `repeat(${perRow}, 1fr)`,
                gap: `${gapEm}em`,
                ...(portrait ? { width: '100%' } : {}),
                ...(half ? { width: '52%', marginLeft: design.stampBlock === 'right' ? 'auto' : 0, marginRight: design.stampBlock === 'left' ? 'auto' : 0 } : {}),
              }}
            >
              {slots.map(renderStamp)}
            </div>
          </div>
          {showFooter ? (
            <div className="lc-footer">
              <span className="lc-progress">{filled} of {required}</span>
              <span className="lc-reward" style={{ color: theme.accent }}>
                {scheme?.rewardLabel || 'Free treatment'} at {required}
              </span>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div
            className="lc-points"
            style={portrait || header !== 'classic'
              ? { flex: portrait ? 1 : undefined, flexDirection: 'column', justifyContent: 'center', textAlign: 'center', gap: '0.8em', marginTop: portrait && header === 'none' ? '6.5em' : headroom }
              : { marginTop: headroom }}
          >
            <div className="lc-ring-wrap" style={portrait ? { width: '5.2em', height: '5.2em' } : undefined}>
              <svg viewBox="0 0 72 72" className="lc-ring">
                <circle cx="36" cy="36" r={R} fill="none" stroke={subInk} strokeWidth="5" />
                <circle
                  cx="36" cy="36" r={R} fill="none"
                  stroke={accent} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={CIRC}
                  strokeDashoffset={CIRC * (1 - pct)}
                  transform="rotate(-90 36 36)"
                  className="lc-ring-fill"
                />
              </svg>
              <span className="lc-ring-icon" style={{ color: accent }}>
                <StampGlyph icon={icon} />
              </span>
            </div>
            <div className="lc-points-meta">
              <span className="lc-balance" style={{ fontFamily: heroFont }}>{balance.toLocaleString()}</span>
              <span className="lc-points-label" style={{ color: subInk }}>points</span>
            </div>
          </div>
          {showFooter ? (
            <div className="lc-footer">
              <span className="lc-progress">{Math.max(0, target - balance).toLocaleString()} to go</span>
              <span className="lc-reward" style={{ color: theme.accent }}>
                {scheme?.pointsLabel || scheme?.rewardLabel || 'Free treatment'} at {target.toLocaleString()}
              </span>
            </div>
          ) : null}
        </>
      )}

      {sceneConfig ? (
        <span className="lc-decor-layer voucher-v2" aria-hidden={!editable}>
          <CustomCardScene
            design={sceneConfig}
            editable={editable}
            selectedId={selectedId}
            selectedIds={selectedIds}
            editingId={editingId}
            onSelect={onElementSelect}
            onElementChange={onElementChange}
            onElementsChange={onElementsChange}
            onElementDuplicate={onElementDuplicate}
            onElementRemove={onElementRemove}
            onElementDoubleClick={onElementDoubleClick}
            onElementContextMenu={onElementContextMenu}
            onCommitEdit={onElementCommitEdit}
            value={null}
            promo={null}
          />
        </span>
      ) : null}
    </div>
  );
};

export default LoyaltyCard;
